import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Brain, CheckCircle, Clock, Search, FileText } from "lucide-react";

interface CoTStep {
  step: number;
  title: string;
  content: string;
  reasoning: string;
  sources?: string[];
}

interface ChainOfThoughtDisplayProps {
  steps: CoTStep[];
  confidenceScore?: number;
  sourcesUsed?: number;
  visible?: boolean;
}

export function ChainOfThoughtDisplay({ 
  steps, 
  confidenceScore, 
  sourcesUsed, 
  visible = true 
}: ChainOfThoughtDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<number[]>([]);

  if (!visible || !steps || steps.length === 0) return null;

  const toggleStep = (stepNumber: number) => {
    setExpandedSteps(prev => 
      prev.includes(stepNumber) 
        ? prev.filter(s => s !== stepNumber)
        : [...prev, stepNumber]
    );
  };

  const getStepIcon = (stepNumber: number) => {
    switch (stepNumber) {
      case 1: return <Search className="h-4 w-4" />;
      case 2: return <FileText className="h-4 w-4" />;
      case 3: return <Brain className="h-4 w-4" />;
      case 4: return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getConfidenceColor = (score?: number) => {
    if (!score) return "bg-muted";
    if (score >= 0.8) return "bg-success";
    if (score >= 0.6) return "bg-warning";
    return "bg-destructive";
  };

  return (
    <Card className="mb-4 border-primary/20 bg-primary/5">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-primary/10 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Brain className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-primary">Raciocínio da IA</h3>
                  <p className="text-sm text-muted-foreground">
                    {steps.length} etapas de análise
                    {sourcesUsed && sourcesUsed > 0 && ` • ${sourcesUsed} fontes consultadas`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {confidenceScore && (
                  <Badge 
                    variant="outline" 
                    className={`${getConfidenceColor(confidenceScore)} text-primary-foreground`}
                  >
                    {Math.round(confidenceScore * 100)}% confiança
                  </Badge>
                )}
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-3 pt-0">
            {steps.map((step) => (
              <Card key={step.step} className="border border-primary/10">
                <Collapsible 
                  open={expandedSteps.includes(step.step)} 
                  onOpenChange={() => toggleStep(step.step)}
                >
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
                            {getStepIcon(step.step)}
                          </div>
                           <div>
                             <h4 className="font-medium text-sm">
                               Etapa {step.step}: {step.title}
                               {step.title.includes('Fundamentação') && step.sources && step.sources.some(s => s.includes('STF') || s.includes('STJ') || s.includes('Web')) && (
                                 <Badge variant="secondary" className="ml-2 text-xs">
                                   🌐 Atual
                                 </Badge>
                               )}
                             </h4>
                             <p className="text-xs text-muted-foreground">
                               {step.content}
                             </p>
                           </div>
                        </div>
                        
                        {expandedSteps.includes(step.step) ? (
                          <ChevronDown className="h-3 w-3 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-3 space-y-2">
                      <div>
                        <h5 className="text-xs font-medium text-muted-foreground mb-1">
                          RACIOCÍNIO:
                        </h5>
                        <p className="text-sm bg-muted/30 p-2 rounded-md">
                          {step.reasoning}
                        </p>
                      </div>
                      
                      {step.sources && step.sources.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-muted-foreground mb-1">
                            FONTES UTILIZADAS:
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {step.sources.map((source, index) => (
                              <Badge 
                                key={index} 
                                variant="secondary" 
                                className="text-xs"
                              >
                                {source}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
            
            <div className="text-xs text-muted-foreground text-center pt-2 border-t border-primary/10">
              ✨ Este raciocínio foi gerado automaticamente pela IA para garantir transparência na análise
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}